<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button
                                        type="button"
                                        class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                                        @click="$router.push(`/theme/${themeId}`)"
                                >
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div class="flex-1">
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">{{ page.name || 'Loading...' }}</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Page ID: {{ pageId }}</p>
                                </div>
                                <button
                                        type="button"
                                        class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        :disabled="isSaving"
                                        @click="savePage"
                                >
                                        {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                </button>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading page...</span>
                </div>

                <!-- Main Content -->
                <main v-else class="p-6">
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Tab Headers -->
                                <div class="flex border-b border-gray-200 overflow-x-auto">
                                        <button
                                                v-for="tab in tabs"
                                                :key="tab.id"
                                                type="button"
                                                class="py-4 px-6 text-sm font-medium whitespace-nowrap border-none bg-transparent cursor-pointer border-b-2 -mb-px"
                                                :class="{ 'text-orange-7 border-orange-7 bg-orange-1': activeTab === tab.id, 'text-gray-500 border-transparent': activeTab !== tab.id }"
                                                @click="activeTab = tab.id"
                                        >
                                                {{ tab.label }}
                                        </button>
                                </div>

                                <!-- Tab Content -->
                                <div class="p-6">
                                        <!-- Properties Tab -->
                                        <div v-if="activeTab === 'properties'">
                                                <div class="mb-6">
                                                        <h3 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">General</h3>
                                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div class="mb-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                                                        <input v-model="page.name" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
                                                                </div>
                                                                <div class="mb-0 min-w-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Template</label>
                                                                        <div class="flex items-center gap-2 min-w-0">
                                                                                <select v-model="selectedTemplateId" class="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm box-border bg-white focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
                                                                                        <option :value="0">None</option>
                                                                                        <option v-for="template in filteredTemplates" :key="template.id" :value="template.id">
                                                                                                {{ template.name || `Template ${template.id}` }}
                                                                                        </option>
                                                                                </select>
                                                                                <button
                                                                                        v-if="page.template"
                                                                                        type="button"
                                                                                        class="inline-flex items-center gap-1.5 bg-orange-50 text-orange-7 border border-orange-200 rounded-md px-3 py-2 text-sm font-medium cursor-pointer hover:bg-orange-100 hover:border-orange-300 transition-colors whitespace-nowrap flex-shrink-0"
                                                                                        @click="goToTemplate(page.template)"
                                                                                >
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                                                                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                                                        </svg>
                                                                                        Edit
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="inline-flex items-center gap-1.5 bg-orange-50 text-orange-7 border border-orange-200 rounded-md px-3 py-2 text-sm font-medium cursor-pointer hover:bg-orange-100 hover:border-orange-300 transition-colors whitespace-nowrap flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        :disabled="isCreatingTemplate"
                                                                                        @click="createTemplate"
                                                                                >
                                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                                        </svg>
                                                                                        {{ isCreatingTemplate ? 'Creating...' : 'New' }}
                                                                                </button>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>

                                                <div class="border-t border-gray-100 pt-6 mb-6">
                                                        <h3 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Dimensions</h3>
                                                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                <div class="mb-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Width</label>
                                                                        <input v-model.number="page.width" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
                                                                </div>
                                                                <div class="mb-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Height</label>
                                                                        <input v-model.number="page.height" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
                                                                </div>
                                                                <div class="mb-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Offset</label>
                                                                        <input v-model.number="page.offset" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
                                                                </div>
                                                        </div>
                                                </div>

                                                <div class="border-t border-gray-100 pt-6 mb-6">
                                                        <h3 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Behavior</h3>
                                                        <div class="flex items-center gap-8">
                                                                <label class="flex items-center gap-2 cursor-pointer">
                                                                        <div class="relative">
                                                                                <input type="checkbox" :checked="!!page.editable" @change="page.editable = ($event.target as HTMLInputElement).checked ? 1 : 0" class="sr-only peer">
                                                                                <div class="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-orange-7 transition-colors"></div>
                                                                                <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-5 transition-transform"></div>
                                                                        </div>
                                                                        <span class="text-sm font-medium text-gray-700">Editable</span>
                                                                </label>
                                                                <label class="flex items-center gap-2 cursor-pointer">
                                                                        <div class="relative">
                                                                                <input type="checkbox" :checked="!!page.movable" @change="page.movable = ($event.target as HTMLInputElement).checked ? 1 : 0" class="sr-only peer">
                                                                                <div class="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-orange-7 transition-colors"></div>
                                                                                <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-5 transition-transform"></div>
                                                                        </div>
                                                                        <span class="text-sm font-medium text-gray-700">Movable</span>
                                                                </label>
                                                        </div>
                                                </div>

                                                <div class="border-t border-gray-100 pt-6">
                                                        <h3 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Appearance</h3>
                                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                <div class="md:col-span-2 mb-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                                                                        <div class="flex items-center gap-3">
                                                                                <input
                                                                                        v-if="page.bgcolor && page.bgcolor !== 'transparent'"
                                                                                        type="color"
                                                                                        :value="page.bgcolor"
                                                                                        class="w-10 h-10 p-0.5 border border-gray-300 rounded-md cursor-pointer bg-white"
                                                                                        @input="page.bgcolor = ($event.target as HTMLInputElement).value"
                                                                                >
                                                                                <div
                                                                                        v-else
                                                                                        class="w-10 h-10 rounded-md border border-gray-300 bg-white flex items-center justify-center cursor-default"
                                                                                        style="background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%); background-size: 10px 10px; background-position: 0 0, 0 5px, 5px -5px, -5px 0px;"
                                                                                ></div>
                                                                                <input
                                                                                        v-model="page.bgcolor"
                                                                                        type="text"
                                                                                        placeholder="#ffffff or transparent"
                                                                                        class="w-40 px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent"
                                                                                >
                                                                        </div>
                                                                </div>
                                                                <div class="mb-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Background Image</label>
                                                                        <div class="flex items-start gap-4">
                                                                                <img
                                                                                        v-if="page.bgimage"
                                                                                        :src="page.bgimage"
                                                                                        alt="Background image"
                                                                                        class="w-24 h-24 rounded object-contain border border-gray-200"
                                                                                >
                                                                                <div v-else class="w-24 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-sm">No image</div>
                                                                                <div class="flex flex-col gap-2">
                                                                                        <button
                                                                                                type="button"
                                                                                                class="bg-gray-100 text-gray-700 border border-gray-300 rounded-md px-4 py-2 text-sm cursor-pointer hover:bg-gray-200"
                                                                                                @click="uploadBgImage"
                                                                                        >
                                                                                                {{ page.bgimage ? 'Change Image' : 'Upload Image' }}
                                                                                        </button>
                                                                                        <button
                                                                                                v-if="page.bgimage"
                                                                                                type="button"
                                                                                                class="bg-red-50 text-red-600 border-none rounded px-3 py-1.5 text-sm cursor-pointer hover:bg-red-100"
                                                                                                @click="page.bgimage = null"
                                                                                        >
                                                                                                Remove
                                                                                        </button>
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                                <div class="mb-0">
                                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Background Pattern</label>
                                                                        <div class="flex items-start gap-4">
                                                                                <img
                                                                                        v-if="page.bgpattern"
                                                                                        :src="page.bgpattern"
                                                                                        alt="Background pattern"
                                                                                        class="w-24 h-24 rounded object-contain border border-gray-200"
                                                                                >
                                                                                <div v-else class="w-24 h-24 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-sm">No pattern</div>
                                                                                <div class="flex flex-col gap-2">
                                                                                        <button
                                                                                                type="button"
                                                                                                class="bg-gray-100 text-gray-700 border border-gray-300 rounded-md px-4 py-2 text-sm cursor-pointer hover:bg-gray-200"
                                                                                                @click="uploadBgPattern"
                                                                                        >
                                                                                                {{ page.bgpattern ? 'Change Pattern' : 'Upload Pattern' }}
                                                                                        </button>
                                                                                        <button
                                                                                                v-if="page.bgpattern"
                                                                                                type="button"
                                                                                                class="bg-red-50 text-red-600 border-none rounded px-3 py-1.5 text-sm cursor-pointer hover:bg-red-100"
                                                                                                @click="page.bgpattern = null"
                                                                                        >
                                                                                                Remove
                                                                                        </button>
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Objects Tab -->
                                        <div v-if="activeTab === 'objects'">
                                                <div class="flex justify-end gap-2 mb-4">
                                                        <button
                                                                type="button"
                                                                class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                :disabled="isAddingObject"
                                                                @click="addObject('photo')"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                </svg>
                                                                {{ isAddingObject ? 'Adding...' : 'Add Photo Object' }}
                                                        </button>
                                                        <button
                                                                type="button"
                                                                class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                :disabled="isAddingObject"
                                                                @click="addObject('text')"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                                </svg>
                                                                {{ isAddingObject ? 'Adding...' : 'Add Text Object' }}
                                                        </button>
                                                </div>
                                                <div v-if="isLoadingObjects" class="flex justify-center items-center py-16 px-4">
                                                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        <span class="ml-3 text-gray-500 text-sm">Loading objects...</span>
                                                </div>
                                                <div v-else class="overflow-x-auto">
                                                        <table class="w-full border-collapse">
                                                                <thead>
                                                                        <tr class="bg-gray-50">
                                                                                <th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
                                                                                <th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Type</th>
                                                                                <th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                                <th class="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
                                                                        </tr>
                                                                </thead>
                                                                <tbody>
                                                                        <tr v-for="obj in pageObjects" :key="obj.id" class="border-b border-gray-200 cursor-pointer hover:bg-gray-50" @click="editObject(obj)">
                                                                                <td class="px-6 py-3 text-sm text-gray-500 font-mono">{{ obj.id }}</td>
                                                                                <td class="px-6 py-3 text-sm text-gray-700">{{ obj.type }}</td>
                                                                                <td class="px-6 py-3 text-sm text-gray-900 font-medium">{{ obj.name || '-' }}</td>
                                                                                <td class="px-6 py-3 text-sm text-right">
                                                                                        <div class="flex justify-end gap-2">
                                                                                                <button
                                                                                                        type="button"
                                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                                        @click.stop="editObject(obj)"
                                                                                                        title="Edit"
                                                                                                >
                                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                                        </svg>
                                                                                                </button>
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                        <tr v-if="pageObjects.length === 0">
                                                                                <td colspan="4" class="text-center py-12 px-4">
                                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                                        </svg>
                                                                                        <p class="text-gray-500 text-sm m-0">No page objects found</p>
                                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Objects will appear here when added to this page</p>
                                                                                </td>
                                                                        </tr>
                                                                </tbody>
                                                        </table>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { httpClient } from 'utils/http';
import TemplateService from 'services/TemplateService';
import Swal from 'sweetalert2';

export default defineComponent({
        name: 'ThemePageEditPage',
        setup() {
                const instance = getCurrentInstance();
                const route = useRoute();
                const router = useRouter();
                const themeId = route.params.id as string;
                const pageId = route.params.pageid as string;

                const page = ref<any>({});
                const pageObjects = ref<any[]>([]);
                const templates = ref<any[]>([]);
                const selectedTemplateId = computed({
                        get: () => page.value.template || 0,
                        set: (val: number) => { page.value.template = val || null; }
                });
                const filteredTemplates = computed(() => {
                        const w = page.value.width;
                        const h = page.value.height;
                        const o = page.value.offset;
                        const selectedId = page.value.template;
                        if (w === undefined || w === null || h === undefined || h === null || o === undefined || o === null) {
                                const selected = templates.value.find(t => t.id === selectedId);
                                return selected ? [selected] : [];
                        }
                        const matches = templates.value.filter(t => t.width === w && t.height === h && t.bleedmargin === o);
                        if (selectedId && !matches.some(t => t.id === selectedId)) {
                                const selected = templates.value.find(t => t.id === selectedId);
                                if (selected) matches.unshift(selected);
                        }
                        return matches;
                });
                const isLoading = ref(false);
                const isLoadingObjects = ref(false);
                const isAddingObject = ref(false);
                const isSaving = ref(false);
                const isCreatingTemplate = ref(false);
                const activeTab = ref('properties');
                const tabs = [
                        { id: 'properties', label: 'Properties' },
                        { id: 'objects', label: 'Objects' },
                ];

                const savedSnapshot = ref<string>('');
                const takeSnapshot = () => {
                        savedSnapshot.value = JSON.stringify(page.value);
                };
                const isDirty = () => JSON.stringify(page.value) !== savedSnapshot.value;

                const loadPage = async () => {
                        isLoading.value = true;
                        try {
                                const { data } = await httpClient.get(`/api/page/${pageId}`);
                                page.value = data;
                                takeSnapshot();
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load page',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadPageObjects = async (rethrow = false) => {
                        isLoadingObjects.value = true;
                        try {
                                const { data } = await httpClient.get(`/api/page/${pageId}/objects`);
                                pageObjects.value = data;
                        } catch (error: any) {
                                console.error('Failed to load page objects:', error);
                                if (rethrow) throw error;
                        } finally {
                                isLoadingObjects.value = false;
                        }
                };

                const loadReferenceData = async () => {
                        try {
                                const { data } = await httpClient.get<any[]>('/api/template?limit=0');
                                templates.value = data;
                        } catch (error: any) {
                                console.error('Failed to load reference data:', error);
                        }
                };

                const savePage = async () => {
                        isSaving.value = true;
                        try {
                                await httpClient.put(`/api/page/${pageId}`, page.value);
                                takeSnapshot();
                                Swal.fire({
                                        title: 'Saved',
                                        text: 'Page has been saved',
                                        icon: 'success',
                                        timer: 1500,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to save page',
                                        icon: 'error',
                                });
                        } finally {
                                isSaving.value = false;
                        }
                };

                const goToTemplate = (templateId: number) => {
                        router.push(`/template/${templateId}`);
                };

                const createTemplate = async () => {
                        const { value: name, isConfirmed } = await Swal.fire({
                                title: 'Create New Template',
                                input: 'text',
                                inputLabel: 'Template name',
                                inputPlaceholder: 'Enter template name',
                                showCancelButton: true,
                                confirmButtonText: 'Create',
                                inputValidator: (value: string) => (value && value.trim() ? null : 'A name is required'),
                        });
                        if (!isConfirmed || !name || !name.trim()) return;

                        isCreatingTemplate.value = true;
                        try {
                                const newTemplate = await TemplateService.create({
                                        name: name.trim(),
                                        width: page.value.width,
                                        height: page.value.height,
                                        bleedmargin: page.value.offset,
                                } as any);
                                await loadReferenceData();
                                page.value.template = newTemplate.id;
                                const result = await Swal.fire({
                                        title: 'Template Created',
                                        text: 'The template has been created and selected for this page. Remember to save the page to keep the selection. Do you want to edit the template now?',
                                        icon: 'success',
                                        showCancelButton: true,
                                        confirmButtonText: 'Edit Template',
                                        cancelButtonText: 'Stay Here',
                                });
                                if (result.isConfirmed) {
                                        // Persist the new template assignment before navigating away,
                                        // otherwise it would be silently lost.
                                        try {
                                                await httpClient.put(`/api/page/${pageId}`, page.value);
                                                takeSnapshot();
                                        } catch (saveError: any) {
                                                Swal.fire({
                                                        title: 'Error',
                                                        text: saveError?.message
                                                                ? `The page could not be saved before editing the template: ${saveError.message}`
                                                                : 'The page could not be saved before editing the template.',
                                                        icon: 'error',
                                                });
                                                return;
                                        }
                                        goToTemplate(newTemplate.id as unknown as number);
                                }
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create template',
                                        icon: 'error',
                                });
                        } finally {
                                isCreatingTemplate.value = false;
                        }
                };

                const editObject = (obj: any) => {
                        router.push(`/theme/${themeId}/page/${pageId}/object/${obj.id}`);
                };

                const addObject = async (type: 'photo' | 'text') => {
                        isAddingObject.value = true;
                        let createdObjectId: string | number | null = null;
                        try {
                                const { data: createdObject } = await httpClient.post('/api/object', {
                                        pageid: Number(pageId),
                                        type,
                                        name: type === 'photo' ? 'New photo object' : 'New text object',
                                        x_axis: 0,
                                        y_axis: 0,
                                        z_axis: 0,
                                        width: 200,
                                        height: 200,
                                        maxwidth: page.value.width || 200,
                                        maxheight: page.value.height || 200,
                                        rotate: 0,
                                        flip: 0,
                                        flop: 0,
                                        opacity: 1,
                                        borderwidth: 0,
                                        bordercolor: null,
                                        cropx: 0,
                                        cropy: 0,
                                        cropwidth: 200,
                                        cropheight: 200,
                                        editable: 1,
                                        transformable: 1,
                                });
                                createdObjectId = createdObject?.id;
                                if (createdObjectId === undefined || createdObjectId === null) {
                                        throw new Error('Object creation did not return an id');
                                }

                                try {
                                        if (type === 'photo') {
                                                await httpClient.post('/api/objectphoto', {
                                                        objectid: createdObjectId,
                                                });
                                        } else {
                                                await httpClient.post('/api/objecttext', {
                                                        objectid: createdObjectId,
                                                });
                                        }
                                } catch (subError: any) {
                                        // Roll back the orphaned page object so we don't leave
                                        // a pageobject without its type-specific sub-record.
                                        const subMessage = subError?.message
                                                ? `Failed to create the ${type} details: ${subError.message}`
                                                : `Failed to create the ${type} details for the new object`;
                                        let rollbackSucceeded = false;
                                        try {
                                                await httpClient.delete(`/api/object/${createdObjectId}`);
                                                rollbackSucceeded = true;
                                        } catch {
                                                // Reported below.
                                        }
                                        throw new Error(rollbackSucceeded
                                                ? `${subMessage}. The partially created object was removed; please try again.`
                                                : `${subMessage}. Cleanup of the incomplete object (ID ${createdObjectId}) also failed — it may still appear in the list without its ${type} details.`);
                                }

                                // Creation fully succeeded from here on; refresh/navigation
                                // problems must not be reported as a failed creation.
                                try {
                                        await loadPageObjects(true);
                                        await router.push(`/theme/${themeId}/page/${pageId}/object/${createdObjectId}`);
                                } catch {
                                        Swal.fire({
                                                title: 'Object created',
                                                text: `The ${type} object (ID ${createdObjectId}) was created, but the view could not be refreshed. Reload the page to see it.`,
                                                icon: 'warning',
                                        });
                                }
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to add object',
                                        icon: 'error',
                                });
                        } finally {
                                isAddingObject.value = false;
                        }
                };

                const uploadBgImage = () => {
                        const client = instance?.appContext.config.globalProperties.$client;
                        if (!client) {
                                Swal.fire({
                                        title: 'Error',
                                        text: 'File upload service is not available',
                                        icon: 'error',
                                });
                                return;
                        }

                        client.picker({
                                accept: ['image/*'],
                                fromSources: ['local_file_system'],
                                onUploadDone: async (files: any) => {
                                        if (files.filesUploaded.length > 0) {
                                                page.value.bgimage = files.filesUploaded[0].url;
                                        }
                                },
                        }).open();
                };

                const uploadBgPattern = () => {
                        const client = instance?.appContext.config.globalProperties.$client;
                        if (!client) {
                                Swal.fire({
                                        title: 'Error',
                                        text: 'File upload service is not available',
                                        icon: 'error',
                                });
                                return;
                        }

                        client.picker({
                                accept: ['image/*'],
                                fromSources: ['local_file_system'],
                                onUploadDone: async (files: any) => {
                                        if (files.filesUploaded.length > 0) {
                                                page.value.bgpattern = files.filesUploaded[0].url;
                                        }
                                },
                        }).open();
                };

                onBeforeRouteLeave(async () => {
                        if (!isDirty()) return true;
                        const result = await Swal.fire({
                                title: 'Unsaved Changes',
                                text: 'You have unsaved changes on this page. Do you want to save them before leaving?',
                                icon: 'warning',
                                showDenyButton: true,
                                showCancelButton: true,
                                confirmButtonText: 'Save & Leave',
                                denyButtonText: 'Discard Changes',
                                cancelButtonText: 'Stay Here',
                        });
                        if (result.isConfirmed) {
                                isSaving.value = true;
                                try {
                                        await httpClient.put(`/api/page/${pageId}`, page.value);
                                        takeSnapshot();
                                        return true;
                                } catch (error: any) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to save page. Staying on this page so your changes are not lost.',
                                                icon: 'error',
                                        });
                                        return false;
                                } finally {
                                        isSaving.value = false;
                                }
                        }
                        if (result.isDenied) return true;
                        return false;
                });

                const handleBeforeUnload = (event: BeforeUnloadEvent) => {
                        if (isDirty()) {
                                event.preventDefault();
                                event.returnValue = '';
                        }
                };

                onMounted(async () => {
                        window.addEventListener('beforeunload', handleBeforeUnload);
                        await Promise.all([loadPage(), loadPageObjects(), loadReferenceData()]);
                });

                onBeforeUnmount(() => {
                        window.removeEventListener('beforeunload', handleBeforeUnload);
                });

                return {
                        themeId,
                        pageId,
                        page,
                        pageObjects,
                        templates,
                        filteredTemplates,
                        selectedTemplateId,
                        isLoading,
                        isLoadingObjects,
                        isAddingObject,
                        isSaving,
                        activeTab,
                        tabs,
                        savePage,
                        goToTemplate,
                        createTemplate,
                        isCreatingTemplate,
                        editObject,
                        addObject,
                        uploadBgImage,
                        uploadBgPattern,
                };
        },
});
</script>
